import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip, Warning } from '../../components/LessonUI';

const JSFunctions = () => {
    const basicDeclCode = `// Function Declaration (Hoisted)
function calculateTotal(price, tax) {
    return price + (price * tax);
}

// Function Expression (Not Hoisted)
const formatCurrency = function(amount) {
    return \`$\${amount.toFixed(2)}\`;
};

console.log(formatCurrency(calculateTotal(100, 0.08))); // "$108.00"`;

    const parametersCode = `// Modern Parameter Handling
function configureServer(port = 8080, ...middleware) {
    console.log(\`Server starting on port \${port}\`);
    console.log(\`Middleware active: \${middleware.length}\`);
    // 'middleware' is a true Array
}

configureServer(3000, 'logger', 'auth', 'compression');`;

    const executionStackCode = `// Visualization of the Call Stack
function second() {
    console.log('Inside second');
}

function first() {
    second();
    console.log('Back in first');
}

first();
/* 
Stack Trace:
1. Global Execution Context
2. first() pushed
3. second() pushed
4. second() popped
5. first() popped
*/`;

    const closureHandoffCode = `// Function as First-Class Citizens (Currying)
function createHasher(algorithm) {
    return function(data) {
        return \`HASH(\${algorithm}): \${data}\`;
    };
}

const sha256 = createHasher('SHA-256');
console.log(sha256('top-secret-data'));`;

    const pureFunctionsCode = `// Pure Function: No side effects, deterministic
const add = (a, b) => a + b; 

// Impure Function: Depends on external mutable state
let total = 0;
const addToTotal = (v) => {
    total += v; // SIDE EFFECT: Modifies outer scope
    return total;
};`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Architectural Fundamentals</div>
                <h1>Function Fundamentals</h1>
                <p className="hero-lead">
                    Functions are the engine of JavaScript. Beyond simple reuse, they define
                    scope, handle events, and enable the powerful functional programming
                    patterns that define modern web engineering.
                </p>
            </div>

            <div className="content-section">
                <h2>Declaration vs. Expression</h2>
                <p>
                    JavaScript offers multiple ways to define functions, primarily variations of
                    <strong>Declarations</strong> and <strong>Expressions</strong>. The critical
                    difference lies in <strong>Hoisting</strong>.
                </p>
                <CodeBlock language="javascript" code={basicDeclCode} />
                <ProTip><strong> Engineer's Insight:</strong> Function declarations are "fully hoisted,"
                    meaning the entire function body is moved to the top of the scope by the
                    JS engine. Expressions are only hoisted if declared with <code>var</code> (as undefined),
                    but using <code>const</code> ensures they remain in the Temporal Dead Zone until
                    initialized.</ProTip>
            </div>

            <div className="content-section">
                <h2>The Execution Context & Call Stack</h2>
                <p>
                    When a function is invoked, the JavaScript engine creates a new
                    <strong>Execution Context</strong>. This context contains the function's
                    local variables, its <code>this</code> binding, and a reference to its
                    outer lexical environment.
                </p>
                <div className="key-point">
                    Building a mental model of the <strong>Call Stack</strong> is essential. It is
                    a LIFO (Last-In-First-Out) structure that tracks where the program is in
                    its execution.
                </div>
                <CodeBlock language="javascript" code={executionStackCode} />
            </div>

            <div className="content-section">
                <h2>Advanced Parameter Handling</h2>
                <p>
                    ES6 introduced <strong>Default Parameters</strong> and <strong>Rest Parameters</strong>,
                    standardizing how we handle variable arguments and optional inputs.
                </p>
                <CodeBlock language="javascript" code={parametersCode} />
                <div className="best-practice-box">
                    <strong>Best Practice:</strong> Always use Rest Parameters (<code>...args</code>)
                    instead of the legacy <code>arguments</code> object. Rest parameters are actual
                    Arrays, giving you access to methods like <code>.map()</code> and <code>.filter()</code>
                    immediately.
                </div>
            </div>

            <div className="content-section">
                <h2>First-Class Citizens & Functional Power</h2>
                <p>
                    In JavaScript, functions are <strong>First-Class Citizens</strong>. This means
                    they can be assigned to variables, passed into other functions as arguments,
                    and returned from other functions.
                </p>
                <CodeBlock language="javascript" code={closureHandoffCode} />
                <p>
                    This capability allows for <strong>Currying</strong> and <strong>Higher-Order
                        Functions</strong>, forming the backbone of libraries like React and Redux.
                </p>
            </div>

            <div className="content-section">
                <h2>The Pursuit of Purity</h2>
                <p>
                    A <strong>Pure Function</strong> is a function that, given the same input,
                    will always return the same output and has no observable side effects (like
                    modifying a global variable or performing I/O).
                </p>
                <CodeBlock language="javascript" code={pureFunctionsCode} />
                <Warning><strong> Caution:</strong> Excessive side effects make code difficult to test
                    and debug. Aim to keep your core logic "Pure" and isolate "Impure" operations
                    (like API calls) to the edges of your application.</Warning>
            </div>

            <KeyFacts><ul>
                    <li><strong>Use Descriptive Verbs:</strong> Name functions like <code>fetchUser</code> or <code>isValidEmail</code>.</li>
                    <li><strong>Keep Functions Small:</strong> Aim for the "Single Responsibility Principle."</li>
                    <li><strong>Avoid Deep Nesting:</strong> Use early returns (guard clauses) to reduce indentation.</li>
                    <li><strong>Embrace Immutability:</strong> Don't modify parameters; return new versions instead.</li>
                    <li><strong>Document Intent:</strong> Use JSDoc for complex logic signatures.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSFunctions;


